import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getCardStats, getRecentPurchases, getNonPsaStats } from '@/api/card';
import { getListing } from '@/api/listing';
import { getRelatedCards } from '@/api/products';
import { addNonPsaWatch, removeNonPsaWatch } from '@/api/watches';
import { getMyProfile } from '@/api/profile';
import useAuthStore from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ToastProvider';
import { redirectToLogin } from '@/utils/authUtils';
import Breadcrumb from '@/components/Breadcrumb';
import CardGallerySection from '@/sections/card/CardGallerySection';
import ListingBuyInfoSection from '@/sections/listing/ListingBuyInfoSection';
import RecentPurchasesSection from '@/sections/card/RecentPurchasesSection';
import CardParametersSection from '@/sections/card/CardParametersSection';
import PriceChartSection from '@/sections/card/PriceChartSection';
import RelatedCardsSection from '@/sections/card/RelatedCardsSection';
import FaqSection from '@/sections/card/FaqSection';
import ReportDialog from '@/components/ReportDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import BindPhoneDialog from '@/components/BindPhoneDialog';
import { TooltipProvider } from '@/components/Tooltip';
import { getCards } from '@/api/products';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import usePenaltyGuard from '@/hooks/usePenaltyGuard';

const ListingDetailPageContent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isSoldOutDialogOpen, setIsSoldOutDialogOpen] = useState(false);
  const [isOwnListingDialogOpen, setIsOwnListingDialogOpen] = useState(false);
  const [isPhoneDialogOpen, setIsPhoneDialogOpen] = useState(false);
  const [phoneDialogCallback, setPhoneDialogCallback] = useState(null);
  const { user } = useAuthStore();

  // Get authentication state
  const accessToken = useAuthStore((state) => state.accessToken);
  const { ensureActionAllowed, penaltyDialogProps } = usePenaltyGuard();

  const { data: listing, isLoading: isLoadingListing } = useQuery({
    queryKey: ['listing', id],
    queryFn: () => getListing(id),
  });

  // Only load profile when user is logged in
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getMyProfile,
    enabled: !!accessToken, // Only fetch when user is authenticated
  });

  const cardId = listing?.data?.product?.id;
  const brandId = listing?.data?.product?.brand?.id;
  const productType = listing?.data?.product?.type || 'SINGLE_CARD';

  // Determine listingType for the API based on the product type
  const listingType = productType === 'SINGLE_CARD' ? 
    (listing?.data?.isPsa ? 'PSA_SINGLE_CARD' : 'NON_PSA_SINGLE_CARD') : 
    'CARD_BOX';

  // Fetch recent purchases for the product, not the listing
  const { data: recentPurchasesData, isLoading: isLoadingPurchases } = useQuery({
    queryKey: ['recentPurchases', cardId, listingType],
    queryFn: () => getRecentPurchases(cardId, listingType),
    enabled: !!cardId && !!listingType,
  });

  const { data: cardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['cardStats', cardId],
    queryFn: () => getCardStats(cardId),
    enabled: !!cardId,
  });
  
  // Fetch non-PSA stats for non-PSA listings
  const { data: nonPsaStats, isLoading: isLoadingNonPsaStats } = useQuery({
    queryKey: ['nonPsaStats', cardId],
    queryFn: () => getNonPsaStats(cardId),
    enabled: !!cardId && !listing?.data?.isPsa,
  });
  
  // Fetch related cards
  const { data: relatedCardsData, isLoading: isLoadingRelatedCards } = useQuery({
    queryKey: ['relatedCards', cardId],
    queryFn: () => getRelatedCards(cardId),
    enabled: !!cardId,
  });

  const {
    data: boxCards,
    isLoading: isBoxLoading,
  } = useQuery({
    queryKey: ['boxCards', listing?.data?.product?.set?.code],
    queryFn: () =>
      getCards({
        setCode: listing?.data?.product?.set?.code,
        type: 'CARD_BOX',
        sort: 'POPULARITY',
        direction: 'DESC',
        limit: 20,
      }),
    enabled: !!id && productType === 'SINGLE_CARD' && !!listing?.data?.product?.set?.code,
  });

  // Add watch mutation
  const addWatchMutation = useMutation({
    mutationFn: () => addNonPsaWatch(cardId, id),
    onSuccess: () => {
      queryClient.invalidateQueries(['listing', id]);
    },
    onError: (error) => {
      toast(`Error: ${error.message}`);
    }
  });
  
  // Remove watch mutation
  const removeWatchMutation = useMutation({
    mutationFn: () => removeNonPsaWatch(cardId, id),
    onSuccess: () => {
      queryClient.invalidateQueries(['listing', id]);
    },
    onError: (error) => {
      toast(`Error: ${error.message}`);
    }
  });

  const handleToggleWatch = (isFavorited) => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    if (isFavorited) {
      addWatchMutation.mutate();
    } else {
      removeWatchMutation.mutate();
    }
  };

  const handleReportClick = () => {
    setIsReportDialogOpen(true);
  };

  const handleReportSubmit = (optionId) => {
    // This will be replaced with an actual API call later
    console.log(`Reported card ${id} with violation option: ${optionId}`);
    // Example of API call that would be made:
    // reportCard(id, { violationId: optionId })
  };

  const handleBuyClick = async () => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    // Check if user is the seller of this listing
    if (listing?.data?.isViewerSeller) {
      setIsOwnListingDialogOpen(true);
      return;
    }

    // Check if user has phone bound
    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleBuyClick());
      setIsPhoneDialogOpen(true);
      return;
    }

    if (!ensureActionAllowed()) {
      return;
    }

    // Re-fetch the listing data to check if it's still available
    await queryClient.invalidateQueries(['listing', id]);
    const updatedListing = await queryClient.fetchQuery({
      queryKey: ['listing', id],
      queryFn: () => getListing(id)
    });

    if (updatedListing?.data?.status === 'SOLD') {
      setIsSoldOutDialogOpen(true);

      // Replace the current data in the cache with the updated data
      queryClient.setQueryData(['listing', id], updatedListing);
      return;
    }

    navigate(`/card/${updatedListing.data.product.id}/purchase/?listing=${id}&price=${updatedListing.data.price}&condition=${updatedListing.data.condition}`);
  };

  const handleAttemptList = () => {
    // Check if user is logged in first
    if (!accessToken) {
      redirectToLogin(navigate, location);
      return;
    }

    // Check if user has a verified phone
    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleAttemptList());
      setIsPhoneDialogOpen(true);
      return;
    }

    if (!ensureActionAllowed()) {
      return;
    }

    // Navigate to sell page
    navigate(`/card/${cardId}/sell?NON_PSA`);
  };

  const handleSoldOutDialogClose = () => {
    setIsSoldOutDialogOpen(false);
  };

  const handleOwnListingDialogClose = () => {
    setIsOwnListingDialogOpen(false);
  };

  const handlePhoneDialogClose = (success) => {
    setIsPhoneDialogOpen(false);
    queryClient.invalidateQueries(['user-profile']);
    
    // If binding was successful and we have a callback, execute it
    if (success && phoneDialogCallback) {
      phoneDialogCallback();
    }
    setPhoneDialogCallback(null);
  };

  // Only show loading for profile if user is authenticated
  const shouldWaitForProfile = accessToken && isLoadingProfile;

  if (isLoadingListing || isLoadingStats || isLoadingNonPsaStats || shouldWaitForProfile) {
    return (
      <div className='container mx-auto flex justify-center items-center'>
        <span>{t('common.loading')}</span>
      </div>
    );
  }
  
  // Process related cards - no need to filter
  const relatedCards = relatedCardsData?.data || [];

  // Get recent purchases data
  const recentPurchases = recentPurchasesData?.data || [];

  // Get watch status directly from listing data
  const isWatched = listing?.data?.isViewerWatching || false;
  
  // Get salesVolume based on listing type
  const salesVolume = listing?.data?.isPsa 
    ? cardStats?.data?.salesVolume 
    : nonPsaStats?.data?.salesVolume;

  // Transform API data to match the UI requirements
  const transformedData = {
    id: listing?.data?.id,
    cardId: listing?.data?.product?.id,
    name: `${listing?.data?.product?.localizedRawName || listing?.data?.product?.localizedName}${listing?.data?.product?.type !== 'CARD_BOX' && listing?.data?.product?.rarityInfo?.sub?.name ? ` (${listing.data.product.rarityInfo.sub.name})` : ''}`,
    subName: listing?.data?.product?.name,
    desc: listing?.data?.description,
    price: listing?.data?.price,
    condition: listing?.data?.condition,
    isOfficial: listing?.data?.isOfficial,
    brandId: listing?.data?.product?.brand?.id,
    brand: listing?.data?.product?.brand?.localizedName,
    brandImage: listing?.data?.product?.brand?.logo?.url,
    canList: !listing?.data?.product?.onlyOfficial || (user && user?.roles?.includes('OFFICIAL')),
    parameters: {
      brand: listing?.data?.product?.brand?.localizedName,
      type: listing?.data?.product?.type,
      code: listing?.data?.product?.code,
      set: listing?.data?.product?.set?.localizedName,
      releaseDate: listing?.data?.product?.releaseDate,
    },
    isSold: listing?.data?.status === 'SOLD',
    images: listing?.data?.images ? [
      listing?.data?.images.front?.url,
      listing?.data?.images.back?.url,
      listing?.data?.images.frontTopLeft?.url,
      listing?.data?.images.frontTopRight?.url,
      listing?.data?.images.frontBottomLeft?.url,
      listing?.data?.images.frontBottomRight?.url,
      listing?.data?.images.damage?.url,
      ...(listing?.data?.images.extras?.map(img => img.url) || [])
    ].filter(Boolean) : [],
    recentPurchases,
    relatedCards: relatedCards,
  };

  const breadcrumbItems = [
    { label: t('common.home'), href: '/' },
    { label: t('home.sections.hot_cards'), href: '/' },
    { label: transformedData.name },
  ];

  return (
    <div className="container py-8">
      <Breadcrumb items={breadcrumbItems} />
      
      <div className="mt-6 grid grid-cols-5 gap-12">
        {/* Left Column */}
        <div className="col-span-3 relative after:content-[''] after:absolute after:w-px after:bg-[#f4f4f4] after:top-0 after:bottom-0 after:right-[-24px]">
          <CardGallerySection images={transformedData.images} isSold={transformedData.isSold} enablePreview={true} listingId={id} />
          <div className="w-full">
            <RecentPurchasesSection 
              purchases={transformedData.recentPurchases}
              salesVolume={salesVolume}
            />
            <CardParametersSection
              showReport
              onReportClick={handleReportClick} 
              parameters={transformedData.parameters}
              rarityInfo={listing?.data?.product?.rarityInfo}
            />
            <PriceChartSection 
              cardId={cardId} 
              cardType={productType} 
              listingType={listingType} 
            />
            <RelatedCardsSection cards={transformedData.relatedCards} cardId={cardId} />
            <FaqSection />
          </div>
        </div>

        <div className="col-span-2">
          {/* Right Column */}
          <ListingBuyInfoSection
            cardId={transformedData.cardId}
            name={transformedData.name}
            localizedRawName={listing?.data?.product?.localizedRawName}
            subName={transformedData.subName}
            desc={transformedData.desc}
            notes={listing?.data?.product?.notes}
            code={listing?.data?.product?.code}
            rawCode={listing?.data?.product?.rawCode}
            rarityInfo={listing?.data?.product?.rarityInfo}
            set={listing?.data?.product?.set}
            brandId={transformedData.brandId}
            brand={transformedData.brand}
            brandImage={transformedData.brandImage}
            price={transformedData.price}
            canList={transformedData.canList}
            isOfficial={transformedData.isOfficial}
            condition={transformedData.condition}
            boxCards={productType === 'SINGLE_CARD' && !isBoxLoading ? boxCards?.data || [] : []}
            psaStats={cardStats?.data?.psaStats || []}
            conditionStats={cardStats?.data?.conditionStats || []}
            inventory={cardStats?.data?.inventory}
            isSold={transformedData.isSold}
            onBuyClick={handleBuyClick}
            onAttemptList={handleAttemptList}
            listingId={id}
            isFavorited={isWatched}
            onFavoriteToggle={handleToggleWatch}
          />
        </div>
      </div>

      <ReportDialog
        isOpen={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
        onConfirm={handleReportSubmit}
      />
      
      <ConfirmDialog
        isOpen={isSoldOutDialogOpen}
        onConfirm={handleSoldOutDialogClose}
        title={t('listing.sold_out_dialog.title')}
        description={t('listing.sold_out_dialog.description')}
        confirmText={t('listing.sold_out_dialog.confirm')}
      />

      <ConfirmDialog
        isOpen={isOwnListingDialogOpen}
        onConfirm={handleOwnListingDialogClose}
        title={t('listing.own_listing_dialog.title')}
        description={t('listing.own_listing_dialog.description')}
        confirmText={t('listing.own_listing_dialog.confirm')}
      />

      {isPhoneDialogOpen && (
        <BindPhoneDialog onClose={handlePhoneDialogClose} />
      )}
      <PenaltyRestrictionDialog {...penaltyDialogProps} />
    </div>
  );
};

// Wrapper with TooltipProvider
const ListingDetailPage = () => {
  return (
    <TooltipProvider>
      <ListingDetailPageContent />
    </TooltipProvider>
  );
};

export default ListingDetailPage; 
